import { useEffect, useState } from 'react';
import { pagoService, prestamoService } from '../../services/api';
import '../../styles/Reportes.css';

const IngresosEgresos = () => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    // Establecer fechas por defecto (mes actual)
    const hoy = new Date();
    const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);

    setFechaInicio(primerDia.toISOString().split('T')[0]);
    setFechaFin(hoy.toISOString().split('T')[0]);
  }, []);

  const cargarDatos = async () => {
    if (!fechaInicio || !fechaFin) {
      alert('Por favor selecciona ambas fechas');
      return;
    }

    setCargando(true);
    try {
      // Cargar pagos (ingresos) y préstamos (egresos) en paralelo
      const [responsePagos, responsePrestamos] = await Promise.all([
        pagoService.getAll(),
        prestamoService.getAll()
      ]);

      const todosPagos = responsePagos.data.pagos || [];
      const todosPrestamos = responsePrestamos.data.prestamos || [];

      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      fin.setHours(23, 59, 59);

      // Filtrar pagos por fecha
      const pagosFiltrados = todosPagos.filter(p => {
        const fechaPago = new Date(p.fecha_pago);
        return fechaPago >= inicio && fechaPago <= fin;
      });

      // Filtrar préstamos por fecha
      const prestamosFiltrados = todosPrestamos.filter(p => {
        const fechaPrestamo = new Date(p.fecha_inicio);
        return fechaPrestamo >= inicio && fechaPrestamo <= fin;
      });

      // Calcular ingresos
      const totalIngresos = pagosFiltrados.reduce((sum, p) => sum + p.monto_total, 0);
      const capitalRecuperado = pagosFiltrados.reduce((sum, p) => sum + (p.monto_capital || 0), 0);
      const interesRecuperado = pagosFiltrados.reduce((sum, p) => sum + (p.monto_interes || 0), 0);
      const moraRecuperada = pagosFiltrados.reduce((sum, p) => sum + (p.monto_mora || 0), 0);

      // Calcular egresos
      const totalEgresos = prestamosFiltrados.reduce((sum, p) => sum + p.monto_prestado, 0);

      // Calcular balance
      const balance = totalIngresos - totalEgresos;
      const utilidad = interesRecuperado + moraRecuperada;

      setDatos({
        ingresos: {
          total: totalIngresos,
          capital: capitalRecuperado,
          interes: interesRecuperado,
          mora: moraRecuperada,
          cantidad: pagosFiltrados.length,
          pagos: pagosFiltrados
        },
        egresos: {
          total: totalEgresos,
          cantidad: prestamosFiltrados.length,
          prestamos: prestamosFiltrados
        },
        balance,
        utilidad
      });
    } catch (error) {
      alert('Error al cargar datos');
      console.error(error);
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-DO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <div className="reporte-detalle">
      <div className="reporte-header">
        <h1>� Ingresos y Egresos</h1>
        <p>Balance financiero de pagos recibidos vs préstamos otorgados</p>
      </div>

      <div className="filtros-container">
        <div className="filtro-grupo">
          <label>Fecha Inicial:</label>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="input-text"
          />
        </div>

        <div className="filtro-grupo">
          <label>Fecha Final:</label>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            className="input-text"
          />
        </div>

        <button onClick={cargarDatos} className="btn-buscar">
          Generar Reporte
        </button>
      </div>

      {cargando && (
        <div className="loading">Generando reporte financiero...</div>
      )}

      {!cargando && datos && (
        <>
          <div className="info-prestamo">
            <h3>Período: {formatearFecha(fechaInicio)} - {formatearFecha(fechaFin)}</h3>
          </div>

          <div className="resumen-cards">
            <div className="card-resumen" style={{ borderLeft: '4px solid #27ae60' }}>
              <div className="card-icono">💵</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#27ae60' }}>
                  ${datos.ingresos.total.toFixed(2)}
                </div>
                <div className="card-label">Total Ingresos</div>
                <small>{datos.ingresos.cantidad} pagos recibidos</small>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #e74c3c' }}>
              <div className="card-icono">💸</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#e74c3c' }}>
                  ${datos.egresos.total.toFixed(2)}
                </div>
                <div className="card-label">Total Egresos</div>
                <small>{datos.egresos.cantidad} préstamos otorgados</small>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: `4px solid ${datos.balance >= 0 ? '#27ae60' : '#e74c3c'}` }}>
              <div className="card-icono">{datos.balance >= 0 ? '📈' : '📉'}</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: datos.balance >= 0 ? '#27ae60' : '#e74c3c' }}>
                  ${datos.balance.toFixed(2)}
                </div>
                <div className="card-label">Balance Neto</div>
                <small>{datos.balance >= 0 ? 'Superávit' : 'Déficit'}</small>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #3498db' }}>
              <div className="card-icono">�</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#3498db' }}>
                  ${datos.utilidad.toFixed(2)}
                </div>
                <div className="card-label">Utilidad Bruta</div>
                <small>Interés + Mora</small>
              </div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '30px' }}>
            <h3>📊 Desglose de Ingresos</h3>
            <div className="tabla-container">
              <table className="tabla-reporte">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>Porcentaje</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Capital Recuperado</td>
                    <td>${datos.ingresos.capital.toFixed(2)}</td>
                    <td>{((datos.ingresos.capital / datos.ingresos.total) * 100).toFixed(1)}%</td>
                  </tr>
                  <tr>
                    <td>Interés Cobrado</td>
                    <td className="destacado">${datos.ingresos.interes.toFixed(2)}</td>
                    <td>{((datos.ingresos.interes / datos.ingresos.total) * 100).toFixed(1)}%</td>
                  </tr>
                  <tr>
                    <td>Mora Cobrada</td>
                    <td>${datos.ingresos.mora.toFixed(2)}</td>
                    <td>{((datos.ingresos.mora / datos.ingresos.total) * 100).toFixed(1)}%</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="totales">
                    <td><strong>TOTAL INGRESOS</strong></td>
                    <td><strong>${datos.ingresos.total.toFixed(2)}</strong></td>
                    <td><strong>100%</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '20px' }}>
            <h3>📉 Resumen de Egresos</h3>
            <div className="info-grid">
              <div><strong>Préstamos Otorgados:</strong> {datos.egresos.cantidad}</div>
              <div><strong>Total Desembolsado:</strong> ${datos.egresos.total.toFixed(2)}</div>
              <div><strong>Promedio por Préstamo:</strong> ${datos.egresos.cantidad > 0 ? (datos.egresos.total / datos.egresos.cantidad).toFixed(2) : '0.00'}</div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '20px', background: datos.balance >= 0 ? '#d4edda' : '#f8d7da' }}>
            <h3 style={{ color: datos.balance >= 0 ? '#155724' : '#721c24' }}>
              💼 Resultado del Período
            </h3>
            <div className="info-grid">
              <div>
                <strong>Balance Neto:</strong>
                <span style={{
                  color: datos.balance >= 0 ? '#155724' : '#721c24',
                  fontSize: '1.2em',
                  marginLeft: '10px'
                }}>
                  ${datos.balance.toFixed(2)}
                </span>
              </div>
              <div>
                <strong>Rentabilidad:</strong>
                <span style={{ fontSize: '1.2em', marginLeft: '10px' }}>
                  {datos.egresos.total > 0 ? ((datos.utilidad / datos.egresos.total) * 100).toFixed(1) : '0'}%
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {!cargando && !datos && (
        <div className="sin-datos">
          <p>Selecciona un período y genera el reporte</p>
        </div>
      )}
    </div>
  );
};

export default IngresosEgresos;
